import React, { useEffect, useReducer, useState } from 'react';
import { BikeLights } from '../../BikeLights';
import { CongestionTaxInfo } from '../../CongestionTax/CongestionTaxInfo';
import { Icon } from '../../Icon/Icon';
import { RoadLayerDescription } from '../../RoadLayerDescription/RoadLayerDescription';
import { Section } from '../../Sidebar/Section';
import { Sidebar } from '../../Sidebar/Sidebar';
import { getAppSettings } from '../../Util/globalAppSettings';
import { getUrlMapLayers, setUrlMapLayer } from '../MapLayerUrlState';
import { getUserMapLayers, getUserProp, getUserPropOrDefault, setUserMapLayer, setUserProp } from '../UserSettings';
import { calculateDefaultLayers, createSVGIcon } from '../Util';
import {
    BIKE_SECTION,
    type ISidebarLayerControlProps,
    type LayerSectionId,
    PARKING_SECTION,
    ROAD_CONDITION_SECTION,
    ROAD_SECTION,
    TAPLAN_SECTION,
} from './SidebarLayerControl.types';
import { ToggleButton } from './ToggleButton';
import { ToggleIcon } from './ToggleIcon';

export const SidebarLayerControl: React.FC<ISidebarLayerControlProps> = ({ defaultOpen = true, ...props }) => {
    const hasUserClosedSidebar = getUserPropOrDefault<boolean>('sidebar-closed-' + props.scope, undefined);
    const isOpen = hasUserClosedSidebar !== undefined ? !hasUserClosedSidebar : defaultOpen;
    const userPreferredLayerControlSection = getUserProp('active-section-' + props.scope) as LayerSectionId;
    const initialSection =
        userPreferredLayerControlSection && props.sectionsToShow.includes(userPreferredLayerControlSection)
            ? userPreferredLayerControlSection
            : props.defaultSection;

    const [activeSection, setActiveSection] = useState(initialSection);
    const [open, setOpen] = useState(isOpen);
    // The following state is needed to make sure that the layer control is updated after layer changes
    const [layerUpdatePending, setLayerUpdatePending] = useState(true);
    const [, forceUpdate] = useReducer((x) => x + 1, 0);

    useEffect(() => {
        props.map.on('popupopen', () => setOpenState(false));

        setActiveLayers(initialSection);
        setLayerUpdatePending(false);

        return () => {
            props.map.off('popupopen', () => setOpenState(false));
        };
    }, []);

    const getSectionHeader = (sectionId: LayerSectionId) => {
        const sectionHeaders = props.sectionHeaders;
        switch (sectionId) {
            case ROAD_SECTION:
                return (sectionHeaders && sectionHeaders[ROAD_SECTION]) || 'Vägtrafik';
            case BIKE_SECTION:
                return (sectionHeaders && sectionHeaders[BIKE_SECTION]) || 'Cykeltrafik';
            case ROAD_CONDITION_SECTION:
                return (sectionHeaders && sectionHeaders[ROAD_CONDITION_SECTION]) || 'Vägväder';
            case PARKING_SECTION:
                return (sectionHeaders && sectionHeaders[PARKING_SECTION]) || 'Parkeringar';
            case TAPLAN_SECTION:
                return (sectionHeaders && sectionHeaders[TAPLAN_SECTION]) || 'Avstängningar och omledningar';
            default:
                return null;
        }
    };

    const onClose = () => {
        setOpenState(false);
    };

    const setOpenState = (open: boolean) => {
        setUserProp('sidebar-closed-' + props.scope, !open);
        setOpen(open);
    };

    const toggleOpen = () => {
        setOpenState(!open);
    };

    const onTabChange = (sectionId: LayerSectionId) => {
        setOpenState(true);
        if (sectionId !== activeSection) {
            onLayerControlSectionChange(sectionId);
        }
    };

    const onLayerChange = (e: React.ChangeEvent<HTMLInputElement>, layerId) => {
        onLayerToggle(layerId, e.target.checked ? 'add' : 'remove');
        forceUpdate();
    };

    const onLayerControlSectionChange = (sectionId: LayerSectionId) => {
        setUserProp('active-section-' + props.scope, sectionId);

        setActiveSection(sectionId);
        setLayerUpdatePending(true);

        setTimeout(() => {
            setActiveLayers(sectionId);
            setLayerUpdatePending(false);
        }, 200);
    };

    const onLayerToggle = (layerId, action: 'add' | 'remove') => {
        props.map[action + 'Layer'](props.layers[layerId].layer);
        if (props.persistLayersInUrl) {
            setUrlMapLayer(layerId, action === 'add', props.defaultOnLayers);
        } else {
            setUserMapLayer(props.scope, layerId, action === 'add');
        }
    };

    const setActiveLayers = (section: LayerSectionId) => {
        const storedLayers = props.persistLayersInUrl ? getUrlMapLayers() : getUserMapLayers(props.scope);
        const activeLayers = calculateDefaultLayers(props.defaultOnLayers, storedLayers);
        Object.values(props.layers)
            .filter((typedLayer) => (typedLayer.sections && !typedLayer.sections.includes(section)) || typedLayer.disabled)
            .forEach((typedLayer) => props.map.removeLayer(typedLayer.layer));
        Object.keys(props.layers)
            .filter(
                (key) =>
                    (!props.layers[key].sections || props.layers[key].sections.includes(section)) &&
                    !props.layers[key].disabled &&
                    activeLayers.includes(key),
            )
            .forEach((key) => props.layers[key].layer.addTo(props.map));
    };

    const renderToggle = (layerId: string, name: string, disabled?: boolean, favoriteFilterId?: string) => {
        const layers = props.layers;
        if (!Object.keys(layers).includes(layerId)) {
            return null;
        }

        const checked = props.map.hasLayer(layers[layerId].layer);

        return (
            <ToggleButton
                key={'checkbox-toggle-' + layerId}
                id={layerId}
                checked={checked}
                onChange={onLayerChange}
                hideInput={layerUpdatePending}
                disabled={disabled}
            >
                <span className="layer-control__label-text-container">
                    {name}
                    {favoriteFilterId && (
                        <ToggleIcon
                            key={'icon-toggle-' + favoriteFilterId}
                            id={favoriteFilterId}
                            name="Mina favoriter"
                            checked={props.map.hasLayer(layers[favoriteFilterId].layer)}
                            checkedIcon="star-gul"
                            uncheckedIcon="star-vit"
                            onChange={onLayerChange}
                            disabled={!checked}
                        />
                    )}
                </span>
                {/* {layers[layerId].icons && <LayerIcons icons={layers[layerId].icons} />} */}
            </ToggleButton>
        );
    };

    const rotateClass = open ? 'icon-rotate-270' : 'icon-rotate-90';

    return (
        <Sidebar
            id="sidebar"
            onTabChange={onTabChange}
            onClose={onClose}
            activeSection={activeSection}
            open={open}
            toggleOpen={toggleOpen}
            toggleIcon={<i className={`layer-control__toggle-icon icon-angle-up ${rotateClass}`} />}
            infoButton={
                props.roadHelpElementId && (
                    <button
                        className="road-help-toggle unstyled-button"
                        aria-label="Visa symbolbeskrivning"
                        type="button"
                        data-bs-toggle="modal"
                        data-bs-target={`#${props.roadHelpElementId}`}
                    >
                        <Icon symbol="icon-legend" />
                    </button>
                )
            }
        >
            {props.sectionsToShow.includes(ROAD_SECTION) && (
                <Section id={ROAD_SECTION} heading={getSectionHeader(ROAD_SECTION)} icon={createSVGIcon('icon-car')}>
                    <div className="layer-control__item-group">
                        <div className="layer-control__item-group-heading">Trafikläget just nu</div>
                        {getAppSettings().enableTomtomRouteMonitoringApi ? (
                            <ToggleButton
                                key={'checkbox-toggle-travel-time-v2'}
                                id={'travel-time-v2'}
                                checked={props.map.hasLayer(props.layers['travel-time-v2'].layer)}
                                onChange={onLayerChange}
                                hideInput={layerUpdatePending}
                            >
                                <RoadLayerDescription colors={['#17D8A8', '#FFB544', '#F23C32', '#811F1F']} lowText="Normalt" highText="Långsamt" />
                            </ToggleButton>
                        ) : (
                            <ToggleButton
                                key={'checkbox-toggle-travel-time'}
                                id={'travel-time'}
                                checked={props.map.hasLayer(props.layers['travel-time'].layer)}
                                onChange={onLayerChange}
                                hideInput={layerUpdatePending}
                            >
                                <RoadLayerDescription colors={['#17D8A8', '#FFB544', '#F23C32', '#811F1F']} lowText="Normalt" highText="Långsamt" />
                            </ToggleButton>
                        )}
                        {renderToggle('road-situations-combined', 'Störningar')}
                        {renderToggle('show-roadwork-situations', 'Pågående vägarbeten')}
                        {renderToggle('all-traffic-cameras', 'Alla trafikkameror')}
                    </div>
                    <div className="layer-control__item-group">
                        <div className="layer-control__item-group-heading">Planerade händelser</div>
                        {renderToggle('show-planned-high-prio-situations', 'Hinder och evenemang')}
                        {renderToggle('show-planned-roadwork-situations', 'Vägarbeten')}
                    </div>
                    <div className="layer-control__item-group">
                        <div className="layer-control__item-group-heading">Trängselskatt</div>
                        <CongestionTaxInfo onContentLoad={() => forceUpdate()} region={props.region} />
                    </div>
                </Section>
            )}
            {props.sectionsToShow.includes(BIKE_SECTION) && (
                <Section id={BIKE_SECTION} heading={getSectionHeader(BIKE_SECTION)} icon={createSVGIcon('icon-bicycle')}>
                    <div className="layer-control__item-group">
                        {renderToggle('show-current-bike-situations', 'Aktuella störningar')}
                        {renderToggle('show-planned-bike-situations', 'Planerade störningar')}
                        {renderToggle('rental-bike', 'Lånecyklar')}
                        {renderToggle('pump-station', 'Cykelpumpar')}
                        {renderToggle('bike-parkings-gbg', 'Parkeringar')}
                        {renderToggle('bike-roads-gbg', 'Cykelvägar')}
                        {renderToggle('bike-roads-vst', 'Cykelvägar')}
                        {renderToggle('winter-bike-roads-danderyd', 'Danderyd')}
                        {renderToggle('winter-bike-roads-lidingo', 'Lidingö')}
                        {renderToggle('winter-bike-roads-nacka', 'Nacka')}
                        {renderToggle('winter-bike-roads-sollentuna', 'Sollentuna')}
                        {renderToggle('winter-bike-roads-solna', 'Solna')}
                        {renderToggle('winter-bike-roads-stockholm', 'Stockholm')}
                        <p>
                            <BikeLights region={props.region} />
                        </p>
                        {props.bikePlannerUrl && (
                            <p>
                                <i className="icon-angle-up icon-rotate-90"></i>
                                <a href={props.bikePlannerUrl} title="Cykelreseplaneraren">
                                    Planera din resa
                                </a>
                            </p>
                        )}
                    </div>
                </Section>
            )}
            {props.sectionsToShow.includes(ROAD_CONDITION_SECTION) && (
                <Section id={ROAD_CONDITION_SECTION} heading={getSectionHeader(ROAD_CONDITION_SECTION)} icon={createSVGIcon('icon-weather')}>
                    <div className="layer-control__item-group">{renderToggle('weather-station', 'Väderstationer')}</div>
                    {!!props.layers['road-condition-combined'] && (
                        <div className="layer-control__item-group">
                            <div className="layer-control__item-group-heading">Väglag</div>
                            <ToggleButton
                                key={'checkbox-toggle-road-condition'}
                                id={'road-condition-combined'}
                                checked={props.map.hasLayer(props.layers['road-condition-combined'].layer)}
                                onChange={onLayerChange}
                                hideInput={layerUpdatePending}
                            >
                                <RoadLayerDescription colors={['#0c0', '#ff0', '#f00']} lowText="Normalt" highText="Besvärligt" />
                            </ToggleButton>
                        </div>
                    )}
                </Section>
            )}
            {props.sectionsToShow.includes(PARKING_SECTION) && (
                <Section id={PARKING_SECTION} heading={getSectionHeader(PARKING_SECTION)} icon={createSVGIcon('icon-parking')}>
                    <div className="layer-control__item-group">
                        {renderToggle('all-shuttle-parking-gbg', 'Pendelparkeringar')}
                        {renderToggle('parkings-gbg-charge', 'Avgiftsbelagda')}
                        {renderToggle('parkings-gbg-time', 'Tidsbegränsade')}
                        {renderToggle('parkings-gbg-disabled', 'Parkering för rörelsehindrade')}
                        {renderToggle('parkings-gbg-mc', 'Motorcykel')}
                        {renderToggle('parkings-gbg-bus-truck', 'Buss och lastbil')}
                        {renderToggle('parkings-sthlm-charge', 'Avgiftsbelagda')}
                        {renderToggle('parkings-sthlm-time', 'Tidsbegränsade')}
                        {renderToggle('parkings-sthlm-disabled', 'Parkering för rörelsehindrade')}
                        {renderToggle('parkings-sthlm-mc', 'Motorcykel')}
                        {renderToggle('parkings-sthlm-bus-truck', 'Buss och lastbil')}
                    </div>
                </Section>
            )}
            {props.sectionsToShow.includes(TAPLAN_SECTION) && (
                <Section id={TAPLAN_SECTION} heading={getSectionHeader(TAPLAN_SECTION)} icon={createSVGIcon('icon-ta-plan-inv')}>
                    <div className="layer-control__item-group">
                        {props.taPlanDescription && <div dangerouslySetInnerHTML={{ __html: props.taPlanDescription }} />}
                        <div className="layer-control__item-group-heading">Visa</div>
                        {renderToggle('ta-plans-current', 'Gällande tillstånd')}
                        {renderToggle('ta-plans-coming', 'Kommande tillstånd')}
                        {renderToggle('ta-plans-completed', 'Nyligen avslutade tillstånd')}
                    </div>
                    <div className="layer-control__item-group">
                        <div className="layer-control__item-group-heading">Visa påverkan på</div>
                        {renderToggle('ta-plans-bike', 'Cykel')}
                        {renderToggle('ta-plans-walk', 'Gång')}
                        {renderToggle('ta-plans-pt', 'Kollektivtrafik')}
                        {renderToggle('ta-plans-motor', 'Motortrafik')}
                    </div>
                    {props.taPlanContact && <div className="mt-4" dangerouslySetInnerHTML={{ __html: props.taPlanContact }} />}
                </Section>
            )}
        </Sidebar>
    );
};

export default SidebarLayerControl;
